import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import { Button } from "antd";
import { useTheme } from "../../../theme/ThemeProvider";

// Main Modal Component
const Modal = ({
  visible = false,
  onClose,
  title,
  children,
  footer,
  size = "md",
  position = "center",
  animation = "scale",
  closable = true,
  maskClosable = true,
  showCloseIcon = true,
  closeOnEsc = true,
  closeIcon = <FiX className="w-5 h-5" />,
  className = "",
  overlayClassName = "",
  contentClassName = "",
  headerClassName = "",
  bodyClassName = "",
  footerClassName = "",
  afterClose,
  afterOpen,
  zIndex = 1000,
  width,
  rounded = "lg",
  backdrop = true,
  backdropOpacity = 0.5,
  destroyOnClose = true,
  forceRender = false,
  animationDuration = 300,
}) => {
  const { isDarkMode } = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Size mappings
  const sizeClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
    full: "max-w-full mx-4",
    auto: "w-auto",
  };

  // Position mappings
  const positionClasses = {
    center: "items-center justify-center",
    top: "items-start justify-center pt-10",
    bottom: "items-end justify-center pb-10",
    left: "items-center justify-start pl-10",
    right: "items-center justify-end pr-10",
    "top-left": "items-start justify-start pt-10 pl-10",
    "top-right": "items-start justify-end pt-10 pr-10",
    "bottom-left": "items-end justify-start pb-10 pl-10",
    "bottom-right": "items-end justify-end pb-10 pr-10",
  };

  // Animation classes
  const getAnimationClasses = () => {
    if (!isVisible) return "opacity-0";

    const transition = `transition-all duration-${animationDuration} ease-[cubic-bezier(0.4,0.0,0.2,1)]`;

    switch (animation) {
      case "fade":
        return `opacity-100 ${transition}`;
      case "slide":
      case "slide-up":
        return `opacity-100 translate-y-0 ${transition}`;
      case "slide-down":
        return `opacity-100 translate-y-0 ${transition}`;
      case "slide-left":
        return `opacity-100 translate-x-0 ${transition}`;
      case "slide-right":
        return `opacity-100 translate-x-0 ${transition}`;
      case "scale":
        return `opacity-100 scale-100 ${transition}`;
      default:
        return "opacity-100";
    }
  };

  const getAnimationInitialClasses = () => {
    switch (animation) {
      case "fade":
        return "opacity-0";
      case "slide":
      case "slide-up":
        return "opacity-0 translate-y-8";
      case "slide-down":
        return "opacity-0 -translate-y-8";
      case "slide-left":
        return "opacity-0 translate-x-8";
      case "slide-right":
        return "opacity-0 -translate-x-8";
      case "scale":
        return "opacity-0 scale-90";
      default:
        return "";
    }
  };

  // Rounded classes
  const roundedClasses = {
    none: "rounded-none",
    sm: "rounded-sm",
    md: "rounded-md",
    lg: "rounded-lg",
    xl: "rounded-xl",
    "2xl": "rounded-2xl",
    "3xl": "rounded-3xl",
    full: "rounded-full",
  };

  // Handle visibility changes
  useEffect(() => {
    if (visible) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
          afterOpen?.();
        });
      });
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        if (destroyOnClose) {
          setShouldRender(false);
        }
        afterClose?.();
      }, animationDuration);
      return () => clearTimeout(timer);
    }
  }, [visible, afterOpen, afterClose, destroyOnClose, animationDuration]);

  // Handle escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (closable && closeOnEsc && e.key === "Escape" && visible) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [closable, closeOnEsc, visible, onClose]);

  // Handle body scroll lock
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [visible]);

  const handleClose = () => {
    if (closable && onClose) {
      onClose();
    }
  };

  const handleMaskClick = (e) => {
    if (e.target === e.currentTarget) {
      if (closable && maskClosable) {
        handleClose();
      }
    }
  };

  if (!forceRender && !shouldRender) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      {backdrop && (
        <div
          className={`fixed inset-0 transition-all duration-300 ${
            isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          style={{
            backgroundColor: `rgba(0, 0, 0, ${backdropOpacity})`,
            zIndex: zIndex,
          }}
          onClick={handleMaskClick}
        />
      )}
      
      {/* Modal Container */}
      <div
        className={`
          fixed inset-0 flex
          ${positionClasses[position] || positionClasses.center}
          pointer-events-none
          transition-all duration-300
          ${isVisible ? "opacity-100" : "opacity-0 pointer-events-none"}
          ${overlayClassName}
          ${className}
        `}
        style={{ zIndex: zIndex + 1 }}
      >
        <div
          className={`
            relative
            w-full
            ${sizeClasses[size] || sizeClasses.md}
            ${roundedClasses[rounded] || roundedClasses.lg}
            shadow-2xl
            transform
            pointer-events-auto
            ${getAnimationInitialClasses()}
            ${isVisible ? getAnimationClasses() : ""}
            ${contentClassName}
          `}
          style={{ 
            width: width,
            backgroundColor: isDarkMode ? '#374151' : '#ffffff',
            color: isDarkMode ? '#f9fafb' : '#1f2937',
          }}
        >
          {/* Header */}
          {(title || (closable && showCloseIcon)) && (
            <div
              className={`
                flex items-center justify-between
                px-6 py-4
                border-b
                ${headerClassName}
              `}
              style={{ 
                borderColor: isDarkMode ? '#4b5563' : '#bbf7d0',
              }}
            >
              {title && (
                <h3 className="text-lg font-semibold text-green-600">{title}</h3>
              )}
              {closable && showCloseIcon && (
                <button
                  onClick={handleClose}
                  className="p-1 text-red-500 transition-all duration-200 transform rounded-full  hover:text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 hover:scale-110 active:scale-95"
                  aria-label="Close"
                >
                  {closeIcon}
                </button>
              )}
            </div>
          )}

          {/* Body */}
          <div className={`px-6 py-4 ${bodyClassName}`}>{children}</div>

          {/* Footer */}
          {footer && (
            <div
              className={`
                flex items-center justify-end gap-3
                px-6 py-4
                border-t
                ${footerClassName}
              `}
              style={{ borderColor: isDarkMode ? '#4b5563' : '#bbf7d0' }}
            >
              {footer}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

// Action Modal Component
export const ActionModal = ({
  type = "info",
  onOk,
  onCancel,
  okText = "OK",
  cancelText = "Cancel",
  okButtonProps = {},
  cancelButtonProps = {},
  closable = true,
  backdropOpacity = 0.5,
  ...props
}) => {
  const typeStyles = {
    info: {
      icon: "ℹ️",
      iconClass: "text-green-600",
    },
    success: {
      icon: "✅",
      iconClass: "text-green-600",
    },
    warning: {
      icon: "⚠️",
      iconClass: "text-yellow-500",
    },
    error: {
      icon: "❌",
      iconClass: "text-red-500",
    },
    confirm: {
      icon: "❓",
      iconClass: "text-green-600",
    },
  };

  const style = typeStyles[type] || typeStyles.info;

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else if (props.onClose) {
      props.onClose();
    }
  };

  const handleOk = () => {
    if (onOk) {
      onOk();
    }
    if (props.onClose && type !== "confirm") {
      props.onClose();
    }
  };

  const defaultFooter = (
    <>
      {(type === "warning" || type === "error" || type === "confirm") && (
        <Button 
          onClick={handleCancel} 
          className="text-green-700 border-green-300 hover:border-green-500 hover:text-green-600"
          {...cancelButtonProps}
        >
          {cancelText}
        </Button>
      )}
      <Button
        type="primary"
        onClick={handleOk}
        className="transition-all duration-200 transform bg-green-600 border-green-600 hover:bg-green-700 hover:scale-105 active:scale-95"
        {...okButtonProps}
      >
        {okText}
      </Button>
    </>
  );

  return (
    <Modal 
      footer={defaultFooter} 
      closable={closable} 
      backdropOpacity={backdropOpacity}
      {...props}
    >
      <div className="flex items-start gap-4">
        <span className={`text-3xl ${style.iconClass}`}>{style.icon}</span>
        <div className="flex-1">{props.children}</div>
      </div>
    </Modal>
  );
};

// Fullscreen Modal Component
export const FullscreenModal = ({ 
  closable = true, 
  backdropOpacity = 0.5,
  ...props 
}) => (
  <Modal
    size="full"
    position="center"
    animation="scale"
    rounded="none"
    closable={closable}
    backdropOpacity={backdropOpacity}
    {...props}
    className="h-screen"
    contentClassName="h-full"
    bodyClassName="h-full overflow-y-auto"
    headerClassName="border-green-100"
  />
);

// Non-closable Modal Component
export const NonClosableModal = ({ backdropOpacity = 0.5, ...props }) => (
  <Modal
    closable={false}
    maskClosable={false}
    showCloseIcon={false}
    closeOnEsc={false}
    backdropOpacity={backdropOpacity}
    {...props}
    headerClassName="border-green-100"
  />
);

// Export all components
export { Modal };
export default Modal;